<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccountFeatures } from 'dashboard/composables/useAccountFeatures';
import Draggable from 'vuedraggable';

import UpdateLeadsToolAPI from 'dashboard/api/integrations/mip/update_leads_tool';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ScanEffect from 'dashboard/components-next/scan-effect/ScanEffect.vue';
import AddFieldDrawer from './AddFieldDrawer.vue';

const { t } = useI18n();
const { hasMarketingCloudTenantId } = useAccountFeatures();

// ========== 常量 ==========
const MAX_FIELDS = 20;

// 假数据（用于空状态展示）- 移到外部避免重复创建
const PLACEHOLDER_FIELDS = Object.freeze([
  {
    attribute_key: 'name',
    attribute_display_name: '姓名',
    attribute_description: '用户的真实姓名',
    attribute_display_type: 'text',
  },
  {
    attribute_key: 'mobile',
    attribute_display_name: '手机号',
    attribute_description: '用户的联系电话',
    attribute_display_type: 'text',
  },
  {
    attribute_key: 'email',
    attribute_display_name: '邮箱',
    attribute_description: '用户的电子邮箱',
    attribute_display_type: 'text',
  },
]);

// ========== 状态 ==========
const isLoading = ref(true);
const isSaving = ref(false);
const isSyncing = ref(false);
const showScanLine = ref(false);

// MIP 绑定状态
const isMipNotConfigured = ref(false);

// 字段数据
const mipFields = ref([]);
const selectedFields = ref([]);

// 添加字段抽屉
const addFieldDrawerRef = ref(null);

// 编辑 AI 描述状态
const editingFieldKey = ref(null);
const tempDescription = ref('');

// 开始编辑 AI 描述
const startEditDescription = field => {
  if (editingFieldKey.value === field.field_key) return; // 已经在编辑中
  editingFieldKey.value = field.field_key;
  tempDescription.value = field.ai_description || '';
  // 下一帧聚焦输入框
  nextTick(() => {
    const input = document.querySelector(
      `[data-field-key="${field.field_key}"] input`
    );
    input?.focus();
  });
};

// 保存 AI 描述
const saveDescription = async field => {
  if (editingFieldKey.value !== field.field_key) return;

  field.ai_description = tempDescription.value;
  editingFieldKey.value = null;
  await saveConfig();
};

// 取消编辑
const cancelEdit = () => {
  editingFieldKey.value = null;
  tempDescription.value = '';
};

// 判断是否正在编辑某个字段
const isEditing = fieldKey => {
  return editingFieldKey.value === fieldKey;
};

// 是否达到上限
const isAtLimit = computed(() => selectedFields.value.length >= MAX_FIELDS);

// ========== 字段转换工具 ==========
const buildFieldFromConfig = (config, isDeleted = false) => ({
  field_key: config.field_key,
  display_name: config.display_name,
  field_type: config.field_type,
  list_options: config.list_options,
  ai_description: config.ai_description || '',
  position: config.position,
  is_deleted: isDeleted,
});

// ========== 加载数据 ==========
const loadData = async () => {
  isLoading.value = true;

  // 首先检查 MIP 租户是否绑定 - 这是一切的基础
  if (!hasMarketingCloudTenantId.value) {
    isMipNotConfigured.value = true;
    isLoading.value = false;
    return; // 直接阻断，只显示引导页，不发任何请求
  }

  isMipNotConfigured.value = false;

  try {
    // MIP 已绑定，请求本地配置
    const { data } = await UpdateLeadsToolAPI.getConfig();
    const configs = data.fields || [];

    // 构建字段列表（初始状态）
    selectedFields.value = configs
      .map(config => buildFieldFromConfig(config))
      .sort((a, b) => a.position - b.position);

    // 后台独立执行 sync（不阻塞页面）
    runBackgroundSync();
  } catch (error) {
    console.error('Failed to load config:', error);
    useAlert(
      t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.LOAD_ERROR') +
        `: ${error.message}`
    );
  } finally {
    isLoading.value = false;
  }
};

// ========== 同步逻辑 ==========
// 执行同步 API 并更新字段状态（核心逻辑）
const executeSync = async () => {
  const response = await UpdateLeadsToolAPI.sync();
  const removedSet = new Set(response.data.removed || []);

  // 重新获取配置（display_name 可能被后端更新）
  const { data } = await UpdateLeadsToolAPI.getConfig();
  const configs = data.fields || [];

  // 重新构建字段列表
  selectedFields.value = configs
    .map(config =>
      buildFieldFromConfig(config, removedSet.has(config.field_key))
    )
    .sort((a, b) => a.position - b.position);

  return removedSet;
};

// 后台同步（带扫描动画）
const runBackgroundSync = async () => {
  if (selectedFields.value.length === 0) return;

  showScanLine.value = true;

  try {
    const minScanTime = new Promise(resolve => setTimeout(resolve, 3600));
    await Promise.all([minScanTime, executeSync()]);
  } catch (error) {
    console.error('Background sync failed:', error);
  } finally {
    showScanLine.value = false;
  }
};

// 抽屉字段是否已加载
const mipFieldsLoaded = ref(false);

// 加载 MIP 字段（抽屉按需加载）
const loadMipFields = async () => {
  if (mipFieldsLoaded.value) return;

  try {
    const { data } = await UpdateLeadsToolAPI.getFields();
    mipFields.value = data.fields || [];
    mipFieldsLoaded.value = true;
  } catch (error) {
    console.error('Failed to load MIP fields:', error);
    useAlert(t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.LOAD_ERROR'));
  }
};

// ========== 可添加字段 ==========
const availableFields = computed(() => {
  const selectedKeys = new Set(selectedFields.value.map(f => f.field_key));
  return mipFields.value.filter(
    f => !selectedKeys.has(f.field_name) && f.identification !== 'channel'
  );
});

// ========== 添加字段 ==========
const openAddDrawer = async () => {
  await loadMipFields();
  addFieldDrawerRef.value?.open();
};

const handleAddField = async fieldKey => {
  const field = mipFields.value.find(f => f.field_name === fieldKey);
  if (!field) return;

  // 添加到已选列表
  selectedFields.value.push({
    field_key: field.field_name,
    display_name: field.display_name,
    field_type: field.field_type,
    list_options: field.list_options,
    ai_description: '',
    position: selectedFields.value.length,
    is_deleted: false,
  });

  // 立即保存
  await saveConfig();
};

const handleBatchAdd = async fieldKeys => {
  fieldKeys.forEach((fieldKey, index) => {
    const field = mipFields.value.find(f => f.field_name === fieldKey);
    if (!field) return;

    selectedFields.value.push({
      field_key: field.field_name,
      display_name: field.display_name,
      field_type: field.field_type,
      list_options: field.list_options,
      ai_description: '',
      position: selectedFields.value.length + index,
      is_deleted: false,
    });
  });

  // 立即保存
  await saveConfig();
};

// ========== 移除字段 ==========
const handleRemoveField = async field => {
  selectedFields.value = selectedFields.value.filter(
    f => f.field_key !== field.field_key
  );
  // 立即保存
  await saveConfig();
};

// ========== 保存配置 ==========
const saveConfig = async () => {
  isSaving.value = true;
  try {
    const fieldsToSave = selectedFields.value.map((f, index) => ({
      field_key: f.field_key,
      display_name: f.display_name,
      field_type: f.field_type,
      list_options: f.list_options,
      ai_description: f.ai_description,
      position: index,
    }));

    await UpdateLeadsToolAPI.updateConfig(fieldsToSave);
    useAlert(t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SAVE_SUCCESS'));
  } catch (error) {
    console.error('Failed to save config:', error);
    const errorMsg = error.response?.data?.error || error.message;
    useAlert(
      t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SAVE_ERROR') +
        `: ${errorMsg}`
    );
  } finally {
    isSaving.value = false;
  }
};

// ========== 同步字段变更（按钮点击） ==========
const syncFields = async () => {
  if (isSyncing.value || selectedFields.value.length === 0) return;

  isSyncing.value = true;
  showScanLine.value = true;

  try {
    const minScanTime = new Promise(resolve => setTimeout(resolve, 3600));
    const [, removedSet] = await Promise.all([minScanTime, executeSync()]);

    // 显示结果提示
    if (removedSet.size > 0) {
      useAlert(
        t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SYNC_HAS_DELETED')
      );
    } else {
      useAlert(t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SYNC_NO_CHANGE'));
    }
  } catch (error) {
    console.error('Failed to sync fields:', error);
    const errorMsg = error.response?.data?.error || error.message;
    useAlert(
      t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SYNC_ERROR') +
        `: ${errorMsg}`
    );
  } finally {
    showScanLine.value = false;
    isSyncing.value = false;
  }
};

// ========== 拖拽排序 ==========
const onDragEnd = async () => {
  // 更新位置并保存
  selectedFields.value.forEach((f, index) => {
    f.position = index;
  });
  await saveConfig();
};

// ========== 生命周期 ==========
onMounted(() => {
  loadData();
});
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <!-- Main Content -->
    <main class="flex-1 w-full overflow-y-auto py-6 flex flex-col gap-6">
      <!-- 加载中 -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <Spinner />
      </div>

      <!-- ========== 情况1：MIP 未绑定 ========== -->
      <template v-else-if="isMipNotConfigured">
        <!-- 虚线介绍区域 -->
        <section class="custom-dashed-border rounded-2xl py-5 px-6">
          <div
            class="flex flex-col md:flex-row items-start md:items-center gap-6"
          >
            <div
              class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden bg-n-slate-2 dark:bg-n-slate-8"
            >
              <img
                :src="'/dashboard/images/integrations/mip.png'"
                alt="MIP 营销云"
                class="w-full h-full object-cover rounded-lg block dark:hidden"
              />
              <img
                :src="'/dashboard/images/integrations/mip-dark.png'"
                alt="MIP 营销云"
                class="w-full h-full object-cover rounded-lg hidden dark:block"
              />
            </div>
            <div class="flex flex-col flex-1 gap-3">
              <p class="text-n-slate-12 text-sm font-medium">
                {{
                  $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.GUIDE.TITLE')
                }}
              </p>
              <p class="text-n-slate-11 text-sm leading-relaxed">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.GUIDE.DESCRIPTION'
                  )
                }}
              </p>
            </div>
          </div>
        </section>

        <!-- 字段配置区域 -->
        <div
          class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
        >
          <div class="flex items-center gap-2 mb-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{
                $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SECTION_TITLE')
              }}
            </h3>
          </div>
          <p class="text-sm text-n-slate-10 mb-4">
            {{
              $t(
                'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SECTION_DESCRIPTION'
              )
            }}
          </p>

          <div class="relative overflow-hidden rounded-lg">
            <!-- 假数据 -->
            <div class="grid gap-3 opacity-50 pointer-events-none p-0.5">
              <div
                v-for="field in PLACEHOLDER_FIELDS"
                :key="field.attribute_key"
                class="flex gap-3 p-4 rounded-2xl outline outline-1 bg-n-solid-1 outline-n-container"
              >
                <div class="flex-shrink-0 flex items-center text-n-slate-9">
                  <Icon icon="i-lucide-grip-vertical" class="size-4" />
                </div>
                <div class="flex-1 flex flex-col gap-2 min-w-0">
                  <div
                    class="flex flex-wrap gap-2 justify-between items-center"
                  >
                    <div class="flex flex-wrap gap-2 items-center min-w-0">
                      <h4 class="text-sm font-medium truncate text-n-slate-12">
                        {{ field.attribute_display_name }}
                      </h4>
                      <div class="w-px h-3 bg-n-strong" />
                      <div
                        class="flex gap-2 items-center text-sm text-n-slate-11"
                      >
                        <Icon icon="i-lucide-align-justify" class="size-4" />
                        <span class="text-sm">{{
                          field.attribute_display_type
                        }}</span>
                      </div>
                      <div class="w-px h-3 bg-n-weak" />
                      <div class="flex items-center gap-1.5 text-n-slate-11">
                        <Icon icon="i-lucide-key-round" class="size-4" />
                        <span class="line-clamp-1 text-sm">{{
                          field.attribute_key
                        }}</span>
                      </div>
                    </div>
                    <div class="flex gap-2 items-center">
                      <div class="h-8 w-8 flex items-center justify-center">
                        <Icon
                          icon="i-lucide-pencil-line"
                          class="size-4 text-n-slate-9"
                        />
                      </div>
                      <div class="w-px h-3 bg-n-strong" />
                      <div class="h-8 w-8 flex items-center justify-center">
                        <Icon
                          icon="i-lucide-trash"
                          class="size-4 text-n-slate-9"
                        />
                      </div>
                    </div>
                  </div>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ field.attribute_description }}
                  </p>
                </div>
              </div>
            </div>
            <!-- 渐变蒙层 + 提示 -->
            <div
              class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-n-surface-1 via-n-surface-1/80 to-transparent pt-8 pb-4"
            >
              <div class="flex flex-col items-center justify-center gap-6">
                <div class="flex flex-col items-center justify-center gap-3">
                  <h2 class="text-3xl font-medium text-center text-n-slate-12">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.NOT_CONFIGURED.TITLE'
                      )
                    }}
                  </h2>
                  <p
                    class="text-base text-center text-n-slate-11 tracking-[0.3px] max-w-xl"
                  >
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.NOT_CONFIGURED.SUBTITLE'
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ========== 情况2：MIP 已绑定 ========== -->
      <template v-else>
        <!-- 空字段警告提示 -->
        <div
          v-if="selectedFields.length === 0"
          class="flex items-start gap-3 p-4 rounded-xl border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-600"
        >
          <div
            class="flex-shrink-0 w-9 h-9 rounded-lg bg-yellow-100 dark:bg-yellow-800 flex items-center justify-center"
          >
            <Icon
              icon="i-lucide-alert-triangle"
              class="size-5 text-yellow-600 dark:text-yellow-400"
            />
          </div>
          <div class="text-sm">
            <p class="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
              {{
                $t(
                  'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.EMPTY_WARNING.TITLE'
                )
              }}
            </p>
            <p class="text-yellow-700 dark:text-yellow-300 leading-relaxed">
              {{
                $t(
                  'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.EMPTY_WARNING.DESCRIPTION'
                )
              }}
            </p>
          </div>
        </div>

        <!-- 虚线介绍区域 -->
        <section class="custom-dashed-border rounded-2xl py-5 px-6">
          <div
            class="flex flex-col md:flex-row items-start md:items-center gap-6"
          >
            <div
              class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden bg-n-slate-2 dark:bg-n-slate-8"
            >
              <img
                :src="'/dashboard/images/integrations/mip.png'"
                alt="MIP 营销云"
                class="w-full h-full object-cover rounded-lg block dark:hidden"
              />
              <img
                :src="'/dashboard/images/integrations/mip-dark.png'"
                alt="MIP 营销云"
                class="w-full h-full object-cover rounded-lg hidden dark:block"
              />
            </div>
            <div class="flex flex-col flex-1 gap-3">
              <p class="text-n-slate-12 text-sm font-medium">
                {{
                  $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.GUIDE.TITLE')
                }}
              </p>
              <p class="text-n-slate-11 text-sm leading-relaxed">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.GUIDE.DESCRIPTION'
                  )
                }}
              </p>
            </div>
          </div>
        </section>

        <!-- 字段配置区域 -->
        <div
          class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
        >
          <div class="flex items-center justify-between gap-2 mb-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{
                $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SECTION_TITLE')
              }}
            </h3>
            <Button
              variant="faded"
              size="sm"
              :label="
                isSyncing
                  ? '检测中...'
                  : $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SYNC_BUTTON')
              "
              :icon="isSyncing ? '' : 'i-lucide-refresh-cw'"
              :disabled="isSyncing || selectedFields.length === 0"
              @click="syncFields"
            >
              <template v-if="isSyncing" #icon>
                <Icon icon="i-lucide-refresh-cw" class="size-4 animate-spin" />
              </template>
            </Button>
          </div>
          <p class="text-sm text-n-slate-10 mb-4">
            {{
              $t(
                'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.SECTION_DESCRIPTION'
              )
            }}
          </p>

          <!-- 添加字段按钮 -->
          <Button
            v-if="!isAtLimit"
            icon="i-lucide-code"
            :label="
              $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_BUTTON')
            "
            class="w-full mb-4"
            @click="openAddDrawer"
          />

          <!-- 已选字段列表 -->
          <div>
            <!-- 空状态：假数据 + 渐变蒙层 -->
            <div
              v-if="selectedFields.length === 0"
              class="relative overflow-hidden rounded-lg"
            >
              <!-- 假数据 -->
              <div class="grid gap-3 opacity-50 pointer-events-none p-0.5">
                <div
                  v-for="field in PLACEHOLDER_FIELDS"
                  :key="field.attribute_key"
                  class="flex gap-3 p-4 rounded-2xl outline outline-1 bg-n-solid-1 outline-n-container"
                >
                  <!-- 拖动手柄占位 -->
                  <div class="flex-shrink-0 flex items-center text-n-slate-9">
                    <Icon icon="i-lucide-grip-vertical" class="size-4" />
                  </div>
                  <div class="flex-1 flex flex-col gap-2 min-w-0">
                    <div
                      class="flex flex-wrap gap-2 justify-between items-center"
                    >
                      <div class="flex flex-wrap gap-2 items-center min-w-0">
                        <h4
                          class="text-sm font-medium truncate text-n-slate-12"
                        >
                          {{ field.attribute_display_name }}
                        </h4>
                        <div class="w-px h-3 bg-n-strong" />
                        <div
                          class="flex gap-2 items-center text-sm text-n-slate-11"
                        >
                          <Icon icon="i-lucide-align-justify" class="size-4" />
                          <span class="text-sm">{{
                            field.attribute_display_type
                          }}</span>
                        </div>
                        <div class="w-px h-3 bg-n-weak" />
                        <div class="flex items-center gap-1.5 text-n-slate-11">
                          <Icon icon="i-lucide-key-round" class="size-4" />
                          <span class="line-clamp-1 text-sm">{{
                            field.attribute_key
                          }}</span>
                        </div>
                      </div>
                      <div class="flex gap-2 items-center">
                        <div class="h-8 w-8 flex items-center justify-center">
                          <Icon
                            icon="i-lucide-pencil-line"
                            class="size-4 text-n-slate-9"
                          />
                        </div>
                        <div class="w-px h-3 bg-n-strong" />
                        <div class="h-8 w-8 flex items-center justify-center">
                          <Icon
                            icon="i-lucide-trash"
                            class="size-4 text-n-slate-9"
                          />
                        </div>
                      </div>
                    </div>
                    <p class="mb-0 text-sm text-n-slate-11">
                      {{ field.attribute_description }}
                    </p>
                  </div>
                </div>
              </div>
              <!-- 渐变蒙层 + 提示 -->
              <div
                class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-n-surface-1 via-n-surface-1/80 to-transparent pt-8 pb-4"
              >
                <div class="flex flex-col items-center justify-center gap-6">
                  <div class="flex flex-col items-center justify-center gap-3">
                    <h2
                      class="text-3xl font-medium text-center text-n-slate-12"
                    >
                      {{
                        $t(
                          'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.EMPTY_TITLE'
                        )
                      }}
                    </h2>
                    <p
                      class="text-base text-center text-n-slate-11 tracking-[0.3px]"
                    >
                      {{
                        $t(
                          'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.EMPTY_SUBTITLE'
                        )
                      }}
                    </p>
                  </div>
                  <Button
                    v-if="!isAtLimit"
                    icon="i-lucide-code"
                    :label="
                      $t(
                        'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_BUTTON'
                      )
                    "
                    @click="openAddDrawer"
                  />
                </div>
              </div>
            </div>

            <!-- 已选字段列表（可拖拽排序） -->
            <div v-else class="relative scan-container">
              <!-- AI 扫描效果 -->
              <ScanEffect :show="showScanLine" text="SCANNING" />

              <Draggable
                v-model="selectedFields"
                handle=".drag-handle"
                animation="200"
                ghost-class="ghost"
                item-key="field_key"
                class="grid gap-3"
                @end="onDragEnd"
              >
                <template #item="{ element: field }">
                  <div
                    class="flex gap-3 p-4 rounded-2xl outline outline-1 transition-all duration-150"
                    :class="
                      field.is_deleted
                        ? 'bg-n-ruby-1 outline-n-ruby-9 animate-shake'
                        : 'bg-n-solid-1 outline-n-container'
                    "
                  >
                    <!-- 拖动手柄 -->
                    <div
                      class="drag-handle flex-shrink-0 flex items-center cursor-grab active:cursor-grabbing text-n-slate-9 hover:text-n-slate-11"
                    >
                      <Icon icon="i-lucide-grip-vertical" class="size-4" />
                    </div>
                    <!-- 字段信息 -->
                    <div class="flex-1 flex flex-col gap-2 min-w-0">
                      <div
                        class="flex flex-wrap gap-2 justify-between items-center"
                      >
                        <div class="flex flex-wrap gap-2 items-center min-w-0">
                          <h4
                            class="text-sm font-medium truncate text-n-slate-12"
                          >
                            {{ field.display_name }}
                          </h4>
                          <div class="w-px h-3 bg-n-strong" />
                          <div
                            class="flex gap-2 items-center text-sm text-n-slate-11"
                          >
                            <Icon
                              icon="i-lucide-align-justify"
                              class="size-4"
                            />
                            <span class="text-sm">{{ field.field_type }}</span>
                          </div>
                          <div class="w-px h-3 bg-n-weak" />
                          <div
                            class="flex items-center gap-1.5 text-n-slate-11"
                          >
                            <Icon icon="i-lucide-key-round" class="size-4" />
                            <span class="line-clamp-1 text-sm">{{
                              field.field_key
                            }}</span>
                          </div>
                        </div>
                        <!-- 操作按钮 -->
                        <div class="flex gap-2 items-center">
                          <Button
                            icon="i-lucide-pencil-line"
                            size="sm"
                            color="slate"
                            ghost
                            @click="startEditDescription(field)"
                          />
                          <div class="w-px h-3 bg-n-strong" />
                          <Button
                            icon="i-lucide-trash"
                            size="sm"
                            color="slate"
                            ghost
                            @click="handleRemoveField(field)"
                          />
                        </div>
                      </div>
                      <!-- AI 描述：点击编辑模式 -->
                      <div
                        :data-field-key="field.field_key"
                        class="flex items-center gap-2"
                        @click.stop="startEditDescription(field)"
                      >
                        <!-- 编辑状态：显示输入框 -->
                        <input
                          v-if="isEditing(field.field_key)"
                          ref="descriptionInput"
                          v-model="tempDescription"
                          type="text"
                          class="flex-1 px-2 py-1 text-sm border border-n-brand rounded bg-n-slate-white dark:bg-n-slate-8 text-n-slate-12 focus:outline-none"
                          :placeholder="
                            $t(
                              'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.AI_DESCRIPTION_PLACEHOLDER'
                            )
                          "
                          @blur="saveDescription(field)"
                          @keyup.enter="saveDescription(field)"
                          @keyup.esc="cancelEdit"
                        />
                        <!-- 非编辑状态：显示文本 -->
                        <span
                          v-else
                          class="flex-1 text-sm truncate cursor-pointer"
                          :class="
                            field.ai_description
                              ? 'text-n-slate-12'
                              : 'text-n-slate-10 italic'
                          "
                        >
                          {{
                            field.ai_description ||
                            $t(
                              'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.AI_DESCRIPTION_PLACEHOLDER'
                            )
                          }}
                        </span>
                      </div>
                      <!-- 已删除字段提示 -->
                      <p
                        v-if="field.is_deleted"
                        class="mb-0 text-sm text-n-ruby-11"
                      >
                        {{
                          $t(
                            'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.FIELD_DELETED'
                          )
                        }}
                      </p>
                    </div>
                  </div>
                </template>
              </Draggable>
            </div>
          </div>
        </div>

        <!-- 添加字段抽屉 -->
        <AddFieldDrawer
          ref="addFieldDrawerRef"
          :available-fields="
            availableFields.map(f => ({
              attribute_key: f.field_name,
              attribute_display_name: f.display_name,
              attribute_description: f.description || '',
              attribute_display_type: f.field_type,
            }))
          "
          :selected-fields="
            selectedFields.map(f => ({
              attribute_key: f.field_key,
            }))
          "
          :max-fields="20"
          @add="handleAddField"
          @batch-add="handleBatchAdd"
        />

        <!-- 提示信息 -->
        <div
          class="bg-n-slate-1 dark:bg-n-slate-7 rounded-2xl border border-n-slate-3 dark:border-n-slate-6 p-4"
        >
          <div class="flex items-start gap-3">
            <Icon
              icon="i-lucide-lightbulb"
              class="size-5 text-n-brand flex-shrink-0 mt-0.5"
            />
            <div class="text-sm text-n-slate-11">
              <p class="font-medium text-n-slate-12 mb-2">
                {{
                  $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.TIPS.TITLE')
                }}
              </p>
              <ul class="list-disc list-inside space-y-1">
                <li>
                  {{
                    $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.TIPS.TIP_1')
                  }}
                </li>
                <li>
                  {{
                    $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.TIPS.TIP_2')
                  }}
                </li>
                <li>
                  {{
                    $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.TIPS.TIP_3')
                  }}
                </li>
                <li>
                  {{
                    $t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.TIPS.TIP_4')
                  }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
    </main>
  </div>
</template>

<style scoped>
.ghost {
  opacity: 0.5;
  background: var(--n-surface-3);
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-2px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(2px);
  }
}

.animate-shake {
  animation: shake 0.5s ease-in-out;
}
</style>
